import { useMemo, useState } from "react";
import type { CalendarViewMode, TeamCalendarEvent, UserListRecord } from "../../shared/models";
import { addDaysToDateInput, formatDate, formatDateRange, formatShortDate, todayDateInputValue } from "../lib/api";

interface CalendarViewProps {
  mode: "personal" | "team";
  users: UserListRecord[];
  events: TeamCalendarEvent[];
  allowedViews: CalendarViewMode[];
  initialView: CalendarViewMode;
  initialDate?: string;
  selectedUserIds?: number[];
  onToggleUserSelection?: (user: UserListRecord) => void;
  onOpenUserProfile?: (user_id: number) => void;
}

interface CalendarRange {
  start_date: string;
  end_date: string;
}

function parseDateParts(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  return { year, month, day };
}

function formatDateParts(year: number, month: number, day: number) {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function utcDateFromInput(value: string) {
  const { year, month, day } = parseDateParts(value);
  return new Date(Date.UTC(year, month - 1, day));
}

function monthStart(value: string) {
  return `${value.slice(0, 7)}-01`;
}

function monthEnd(value: string) {
  const date = utcDateFromInput(monthStart(value));
  date.setUTCMonth(date.getUTCMonth() + 1, 0);
  return formatDateParts(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}

function yearStart(value: string) {
  return `${value.slice(0, 4)}-01-01`;
}

function yearEnd(value: string) {
  return `${value.slice(0, 4)}-12-31`;
}

function startOfWeek(value: string) {
  const date = utcDateFromInput(value);
  const day = date.getUTCDay();
  date.setUTCDate(date.getUTCDate() - day);
  return formatDateParts(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}

function endOfWeek(value: string) {
  return addDaysToDateInput(startOfWeek(value), 6);
}

function listDays(start_date: string, end_date: string) {
  const dates: string[] = [];
  let cursor = start_date;
  while (cursor <= end_date) {
    dates.push(cursor);
    cursor = addDaysToDateInput(cursor, 1);
  }
  return dates;
}

function dateRangeForView(view: CalendarViewMode, anchorDate: string): CalendarRange {
  if (view === "day") {
    return { start_date: anchorDate, end_date: anchorDate };
  }

  if (view === "week") {
    return { start_date: startOfWeek(anchorDate), end_date: endOfWeek(anchorDate) };
  }

  if (view === "month") {
    return { start_date: monthStart(anchorDate), end_date: monthEnd(anchorDate) };
  }

  return { start_date: yearStart(anchorDate), end_date: yearEnd(anchorDate) };
}

function moveAnchorDate(anchorDate: string, view: CalendarViewMode, direction: -1 | 1) {
  if (view === "day") {
    return addDaysToDateInput(anchorDate, direction);
  }

  if (view === "week") {
    return addDaysToDateInput(anchorDate, direction * 7);
  }

  if (view === "month") {
    const monthDate = utcDateFromInput(anchorDate);
    monthDate.setUTCMonth(monthDate.getUTCMonth() + direction);
    return formatDateParts(monthDate.getUTCFullYear(), monthDate.getUTCMonth() + 1, monthDate.getUTCDate());
  } else {
    const yearDate = utcDateFromInput(anchorDate);
    yearDate.setUTCFullYear(yearDate.getUTCFullYear() + direction);
    return formatDateParts(yearDate.getUTCFullYear(), yearDate.getUTCMonth() + 1, yearDate.getUTCDate());
  }
}

function overlapsDate(event: TeamCalendarEvent, date: string) {
  return event.start_date <= date && event.end_date >= date;
}

function eventClassName(kind: TeamCalendarEvent["kind"]) {
  return kind === "personal" ? "calendar-block personal" : "calendar-block commitment";
}

function rangeLabel(view: CalendarViewMode, range: CalendarRange) {
  if (view === "year") {
    return range.start_date.slice(0, 4);
  }
  return formatDateRange(range.start_date, range.end_date);
}

function CalendarToolbar({
  anchorDate,
  allowedViews,
  currentView,
  onAnchorDateChange,
  onViewChange,
}: {
  anchorDate: string;
  allowedViews: CalendarViewMode[];
  currentView: CalendarViewMode;
  onAnchorDateChange: (value: string) => void;
  onViewChange: (view: CalendarViewMode) => void;
}) {
  const range = dateRangeForView(currentView, anchorDate);

  return (
    <div className="calendar-toolbar">
      <div className="calendar-nav-group">
        <button type="button" className="ghost" onClick={() => onAnchorDateChange(moveAnchorDate(anchorDate, currentView, -1))}>Previous</button>
        <button type="button" className="ghost" onClick={() => onAnchorDateChange(todayDateInputValue())}>Today</button>
        <button type="button" className="ghost" onClick={() => onAnchorDateChange(moveAnchorDate(anchorDate, currentView, 1))}>Next</button>
      </div>
      <div className="calendar-toolbar-label">
        <strong>{rangeLabel(currentView, range)}</strong>
      </div>
      <div className="calendar-nav-group">
        <input type="date" value={anchorDate} onChange={(event) => onAnchorDateChange(event.target.value)} />
        <div className="calendar-view-switcher">
          {allowedViews.map((view) => (
            <button
              key={view}
              type="button"
              className={currentView === view ? "nav-link active" : "nav-link"}
              onClick={() => onViewChange(view)}
            >
              {view[0].toUpperCase() + view.slice(1)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function TeamFocusedHeader({
  user,
  onBack,
}: {
  user: UserListRecord;
  onBack: () => void;
}) {
  return (
    <div className="calendar-focused-header">
      <div>
        <button type="button" className="ghost" onClick={onBack}>Back to Team Calendar</button>
        <p className="calendar-breadcrumb">Team Calendar / {user.name}</p>
      </div>
      <div className="calendar-focused-actions">
        <a href={`tel:${user.phone ?? ""}`}>{user.phone ?? "No phone"}</a>
        <a href={`mailto:${user.email}`}>{user.email}</a>
      </div>
    </div>
  );
}

function PersonalMonthView({ user, events, anchorDate }: { user: UserListRecord; events: TeamCalendarEvent[]; anchorDate: string }) {
  const monthRange = dateRangeForView("month", anchorDate);
  const gridStart = startOfWeek(monthRange.start_date);
  const gridEnd = endOfWeek(monthRange.end_date);
  const days = listDays(gridStart, gridEnd);

  return (
    <div className="calendar-personal-month">
      <div className="calendar-month-weekdays">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((weekday) => <span key={weekday}>{weekday}</span>)}
      </div>
      <div className="calendar-month-grid">
        {days.map((date) => {
          const dayEvents = events.filter((event) => overlapsDate(event, date));
          const outOfMonth = date.slice(0, 7) !== anchorDate.slice(0, 7);
          return (
            <div key={date} className={outOfMonth ? "calendar-month-cell muted-cell" : "calendar-month-cell"}>
              <div className="calendar-month-date">{parseDateParts(date).day}</div>
              <div className="calendar-month-events">
                {dayEvents.map((event) => (
                  <div key={`${event.id}-${date}`} className={eventClassName(event.kind)}>
                    <strong>{event.kind === "personal" ? "Personal" : event.project_name ?? "Project"}</strong>
                    <span>{event.label}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <p className="muted">{user.name}</p>
    </div>
  );
}

function PersonalDayListView({ events, range }: { events: TeamCalendarEvent[]; range: CalendarRange }) {
  const days = listDays(range.start_date, range.end_date);

  return (
    <div className="calendar-day-list">
      {days.map((date) => {
        const dayEvents = events.filter((event) => overlapsDate(event, date));
        return (
          <section key={date} className="calendar-day-section">
            <header>
              <strong>{formatDate(date)}</strong>
            </header>
            {dayEvents.length === 0 ? <p className="muted">Open</p> : null}
            {dayEvents.map((event) => (
              <div key={`${event.id}-${date}`} className={eventClassName(event.kind)}>
                <strong>{event.kind === "personal" ? "Personal" : event.project_name ?? "Project"}</strong>
                <span>{event.label}</span>
                <span>{formatDateRange(event.start_date, event.end_date)}</span>
              </div>
            ))}
          </section>
        );
      })}
    </div>
  );
}

function PersonalYearView({ events, anchorDate }: { events: TeamCalendarEvent[]; anchorDate: string }) {
  const year = anchorDate.slice(0, 4);

  return (
    <div className="calendar-year-grid">
      {Array.from({ length: 12 }, (_, index) => index + 1).map((monthNumber) => {
        const month = `${year}-${String(monthNumber).padStart(2, "0")}-01`;
        const range = dateRangeForView("month", month);
        const blockedDays = listDays(range.start_date, range.end_date).filter((date) =>
          events.some((event) => overlapsDate(event, date))
        );
        return (
          <section key={month} className="calendar-year-card">
            <header>
              <strong>{utcDateFromInput(month).toLocaleDateString([], { month: "long", timeZone: "UTC" })}</strong>
            </header>
            <span>{blockedDays.length} blocked day{blockedDays.length === 1 ? "" : "s"}</span>
            <div className="calendar-year-dots">
              {listDays(range.start_date, range.end_date).map((date) => {
                const dayEvents = events.filter((event) => overlapsDate(event, date));
                const className = dayEvents.length === 0
                  ? "calendar-year-dot"
                  : dayEvents.some((event) => event.kind === "personal")
                  ? "calendar-year-dot personal"
                  : "calendar-year-dot commitment";
                return <span key={date} className={className} title={`${formatDate(date)}${dayEvents.length ? `: ${dayEvents.map((event) => event.label).join(", ")}` : ""}`} />;
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function TeamMatrixView({
  users,
  events,
  range,
  selectedUserIds,
  onToggleUserSelection,
  onOpenUserProfile,
}: {
  users: UserListRecord[];
  events: TeamCalendarEvent[];
  range: CalendarRange;
  selectedUserIds: number[];
  onToggleUserSelection?: (user: UserListRecord) => void;
  onOpenUserProfile?: (user_id: number) => void;
}) {
  const days = listDays(range.start_date, range.end_date);

  return (
    <div className="calendar-grid team-matrix">
      <div className="calendar-head row" style={{ gridTemplateColumns: `240px repeat(${days.length}, minmax(120px, 1fr))` }}>
        <div className="calendar-user-cell">Crew</div>
        {days.map((date) => (
          <div key={date} className="calendar-day-cell">
            <strong>{utcDateFromInput(date).toLocaleDateString([], { weekday: "short", timeZone: "UTC" })}</strong>
            <span>{formatShortDate(date)}</span>
          </div>
        ))}
      </div>
      {users.map((user) => (
        <div
          key={user.id}
          className={selectedUserIds.includes(user.id) ? "calendar-row row selected-row" : "calendar-row row"}
          style={{ gridTemplateColumns: `240px repeat(${days.length}, minmax(120px, 1fr))` }}
        >
          <div className="calendar-user-cell">
            {onToggleUserSelection ? (
              <label className="checkbox-field compact">
                <input
                  type="checkbox"
                  checked={selectedUserIds.includes(user.id)}
                  onChange={() => onToggleUserSelection(user)}
                />
                <span>Select</span>
              </label>
            ) : null}
            <strong>{user.name}</strong>
            <span>{user.phone ?? user.email}</span>
            {onOpenUserProfile ? (
              <button type="button" className="ghost compact-button" onClick={() => onOpenUserProfile(user.id)}>
                View Profile
              </button>
            ) : null}
          </div>
          {days.map((date) => {
            const dayEvents = events.filter((event) => event.user_id === user.id && overlapsDate(event, date));
            return (
              <div key={`${user.id}-${date}`} className="calendar-day-cell body">
                {dayEvents.length === 0 ? <span className="empty-cell">Open</span> : null}
                {dayEvents.map((event) => (
                  <div key={`${event.id}-${date}`} className={eventClassName(event.kind)}>
                    <strong>{event.kind === "personal" ? "Personal" : event.project_name ?? "Project"}</strong>
                    <span>{event.label}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function TeamYearView({
  users,
  events,
  anchorDate,
  selectedUserIds,
  onToggleUserSelection,
  onOpenUserProfile,
  onDrillDown,
}: {
  users: UserListRecord[];
  events: TeamCalendarEvent[];
  anchorDate: string;
  selectedUserIds: number[];
  onToggleUserSelection?: (user: UserListRecord) => void;
  onOpenUserProfile?: (user_id: number) => void;
  onDrillDown: (user: UserListRecord, month: string) => void;
}) {
  const year = anchorDate.slice(0, 4);
  const months = Array.from({ length: 12 }, (_, index) => `${year}-${String(index + 1).padStart(2, "0")}-01`);

  return (
    <div className="calendar-grid team-year">
      <div className="calendar-head row" style={{ gridTemplateColumns: "240px repeat(12, minmax(110px, 1fr))" }}>
        <div className="calendar-user-cell">Crew</div>
        {months.map((month) => (
          <div key={month} className="calendar-day-cell">
            <strong>{utcDateFromInput(month).toLocaleDateString([], { month: "short", timeZone: "UTC" })}</strong>
          </div>
        ))}
      </div>
      {users.map((user) => (
        <div
          key={user.id}
          className={selectedUserIds.includes(user.id) ? "calendar-row row selected-row" : "calendar-row row"}
          style={{ gridTemplateColumns: "240px repeat(12, minmax(110px, 1fr))" }}
        >
          <div className="calendar-user-cell">
            {onToggleUserSelection ? (
              <label className="checkbox-field compact">
                <input
                  type="checkbox"
                  checked={selectedUserIds.includes(user.id)}
                  onChange={() => onToggleUserSelection(user)}
                />
                <span>Select</span>
              </label>
            ) : null}
            <strong>{user.name}</strong>
            <span>{user.phone ?? user.email}</span>
            {onOpenUserProfile ? (
              <button type="button" className="ghost compact-button" onClick={() => onOpenUserProfile(user.id)}>
                View Profile
              </button>
            ) : null}
          </div>
          {months.map((month) => {
            const range = dateRangeForView("month", month);
            const monthDays = listDays(range.start_date, range.end_date);
            const monthEvents = events.filter((event) =>
              event.user_id === user.id &&
              !(event.end_date < range.start_date || event.start_date > range.end_date)
            );
            const blockedCount = monthDays.filter((date) => monthEvents.some((event) => overlapsDate(event, date))).length;
            const personalCount = monthEvents.filter((event) => event.kind === "personal").length;
            const commitmentCount = monthEvents.filter((event) => event.kind === "commitment").length;
            return (
              <div key={`${user.id}-${month}`} className="calendar-day-cell body">
                <button type="button" className="month-drilldown-button" onClick={() => onDrillDown(user, month)}>
                  <strong>{blockedCount} day{blockedCount === 1 ? "" : "s"}</strong>
                  <span className="muted">Personal {personalCount}</span>
                  <span className="muted">Projects {commitmentCount}</span>
                </button>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export function CalendarView({
  mode,
  users,
  events,
  allowedViews,
  initialView,
  initialDate,
  selectedUserIds = [],
  onToggleUserSelection,
  onOpenUserProfile,
}: CalendarViewProps) {
  const [currentView, setCurrentView] = useState<CalendarViewMode>(initialView);
  const [anchorDate, setAnchorDate] = useState(initialDate ?? todayDateInputValue());
  const [focusedUserId, setFocusedUserId] = useState<number | null>(null);
  const [teamSnapshot, setTeamSnapshot] = useState<{ view: CalendarViewMode; anchorDate: string } | null>(null);
  const range = useMemo(() => dateRangeForView(currentView, anchorDate), [anchorDate, currentView]);
  const focusedUser = useMemo(
    () => (focusedUserId ? users.find((user) => user.id === focusedUserId) ?? null : null),
    [focusedUserId, users],
  );

  const relevantEvents = useMemo(
    () => events.filter((event) => !(event.end_date < range.start_date || event.start_date > range.end_date)),
    [events, range.end_date, range.start_date],
  );

  if (mode === "personal") {
    const user = users[0];
    if (!user) {
      return (
        <div className="calendar-shell">
          <CalendarToolbar
            anchorDate={anchorDate}
            allowedViews={allowedViews}
            currentView={currentView}
            onAnchorDateChange={setAnchorDate}
            onViewChange={setCurrentView}
          />
          <p className="muted">No calendar data loaded.</p>
        </div>
      );
    }
    return (
      <div className="calendar-shell">
        <CalendarToolbar
          anchorDate={anchorDate}
          allowedViews={allowedViews}
          currentView={currentView}
          onAnchorDateChange={setAnchorDate}
          onViewChange={setCurrentView}
        />
        {currentView === "month" ? <PersonalMonthView user={user} events={relevantEvents} anchorDate={anchorDate} /> : null}
        {currentView === "week" || currentView === "day" ? <PersonalDayListView events={relevantEvents} range={range} /> : null}
        {currentView === "year" ? <PersonalYearView events={events} anchorDate={anchorDate} /> : null}
      </div>
    );
  }

  if (focusedUser) {
    const focusedEvents = relevantEvents.filter((event) => event.user_id === focusedUser.id);
    return (
      <div className="calendar-shell">
        <TeamFocusedHeader
          user={focusedUser}
          onBack={() => {
            setFocusedUserId(null);
            if (teamSnapshot) {
              setCurrentView(teamSnapshot.view);
              setAnchorDate(teamSnapshot.anchorDate);
            }
          }}
        />
        <CalendarToolbar
          anchorDate={anchorDate}
          allowedViews={["day", "week", "month"]}
          currentView={currentView}
          onAnchorDateChange={setAnchorDate}
          onViewChange={setCurrentView}
        />
        {currentView === "month" ? <PersonalMonthView user={focusedUser} events={focusedEvents} anchorDate={anchorDate} /> : null}
        {currentView === "week" || currentView === "day" ? <PersonalDayListView events={focusedEvents} range={range} /> : null}
      </div>
    );
  }

  return (
    <div className="calendar-shell">
      <CalendarToolbar
        anchorDate={anchorDate}
        allowedViews={allowedViews}
        currentView={currentView}
        onAnchorDateChange={setAnchorDate}
        onViewChange={setCurrentView}
      />
      {currentView === "year"
        ? (
          <TeamYearView
            users={users}
            events={events}
            anchorDate={anchorDate}
            selectedUserIds={selectedUserIds}
            onToggleUserSelection={onToggleUserSelection}
            onOpenUserProfile={onOpenUserProfile}
            onDrillDown={(user, month) => {
              setTeamSnapshot({ view: currentView, anchorDate });
              setFocusedUserId(user.id);
              setCurrentView("month");
              setAnchorDate(month);
            }}
          />
        )
        : (
          <TeamMatrixView
            users={users}
            events={relevantEvents}
            range={range}
            selectedUserIds={selectedUserIds}
            onToggleUserSelection={onToggleUserSelection}
            onOpenUserProfile={onOpenUserProfile}
          />
        )}
    </div>
  );
}
